/**
 * TanStack Query hooks for the real WhatsApp inbox (PROD-1849). The page never
 * fetches; it reads these hooks through `useLiveInboxSource`.
 *
 * Polling (no realtime yet): the list and the OPEN thread refetch every 5 s while
 * the page is visible (`refetchIntervalInBackground: false`) and when the window
 * gets focus, so a new patient message shows in the open thread and the unread
 * badge within seconds. Each poll is incremental (`updatedAfter`, with a look-back)
 * and merged into the cache; the merge rules are in ./inbox-cache.ts.
 *
 * Reads opt out of the global error toast (`skipGlobalError`): the page shows the
 * failure in place, and a toast every 5 s while the server is down would be noise.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';

import type { ConversationStatus, Message } from '../inbox-types';
import {
  INBOX_MAX_TAKE,
  type InboxPage,
  fetchConversations,
  fetchMessages,
  markConversationRead,
  sendStaffReply,
} from './inbox-api';
import {
  dropPending,
  isPending,
  mergeConversations,
  mergeMessages,
  nextSyncedAt,
  pendingId,
  pollSince,
  settlePending,
  type ConversationListData,
  type ThreadData,
} from './inbox-cache';
import { describeSendError, shouldRetryInboxRead, type SendFailure } from './inbox-errors';

export const INBOX_POLL_MS = 5_000;
/** Messages per thread page (contract default). */
export const THREAD_PAGE_TAKE = 50;

/** The most pages one poll follows before it gives up and reloads (5 x 100 changed rows). */
export const POLL_MAX_PAGES = 5;

/**
 * One poll, every page: follows `nextCursor` with the same `updatedAfter` (the contract allows it),
 * so a busy inbox (more than one page changed inside the look-back) is still merged.
 * `serverTime` is the FIRST page's: it was stamped before any of these queries ran.
 * `complete` is false when more than `POLL_MAX_PAGES` pages changed.
 */
async function readChanged<T>(
  read: (cursor: string | undefined) => Promise<InboxPage<T>>,
): Promise<{ items: T[]; serverTime: string | null; complete: boolean }> {
  const first = await read(undefined);
  const items = [...first.items];
  let cursor = first.nextCursor;
  for (let pages = 1; cursor && pages < POLL_MAX_PAGES; pages++) {
    const next = await read(cursor);
    items.push(...next.items);
    cursor = next.nextCursor;
  }
  return { items, serverTime: first.serverTime, complete: !cursor };
}

export const inboxKeys = {
  all: ['whatsapp-inbox'] as const,
  lists: () => [...inboxKeys.all, 'list'] as const,
  list: (status?: ConversationStatus) => [...inboxKeys.lists(), status ?? 'all'] as const,
  thread: (conversationId: string) => [...inboxKeys.all, 'thread', conversationId] as const,
  /** PROD-3335: one stored image's bytes (the thumbnail), by message id. */
  attachment: (messageId: string) => [...inboxKeys.all, 'attachment', messageId] as const,
};

const POLLING = {
  refetchInterval: INBOX_POLL_MS,
  refetchIntervalInBackground: false,
  refetchOnWindowFocus: true,
  staleTime: 0,
  retry: shouldRetryInboxRead,
  meta: { skipGlobalError: true },
} as const;

// ---------------------------------------------------------------------------
// 1) Conversation list
// ---------------------------------------------------------------------------

/**
 * The conversation list: the first page (up to 100, the contract maximum), then
 * incremental polls. The board has no pager, so later pages are not read; the
 * first page's `nextCursor` stays in the cache for a pager later.
 */
export function useInboxConversations(opts: { status?: ConversationStatus; enabled?: boolean } = {}) {
  const qc = useQueryClient();
  const key = inboxKeys.list(opts.status);
  return useQuery({
    queryKey: key,
    queryFn: async ({ signal }): Promise<ConversationListData> => {
      const before = qc.getQueryData<ConversationListData>(key);
      if (before?.syncedAt) {
        const since = pollSince(before.syncedAt);
        const changed = await readChanged((cursor) =>
          fetchConversations({ status: opts.status, take: INBOX_MAX_TAKE, updatedAfter: since, cursor, signal }),
        );
        if (changed.complete) {
          const latest = qc.getQueryData<ConversationListData>(key) ?? before;
          return {
            items: mergeConversations(latest.items, changed.items),
            nextCursor: latest.nextCursor,
            syncedAt: nextSyncedAt(latest.syncedAt, changed.serverTime, changed.items.map((c) => c.lastMessageAt)),
          };
        }
        // More than POLL_MAX_PAGES pages changed: reload the first page (below), the server's order.
      }
      const page = await fetchConversations({ status: opts.status, take: INBOX_MAX_TAKE, signal });
      return {
        items: mergeConversations([], page.items),
        nextCursor: page.nextCursor,
        syncedAt: nextSyncedAt(null, page.serverTime, page.items.map((c) => c.lastMessageAt)),
      };
    },
    enabled: opts.enabled ?? true,
    ...POLLING,
  });
}

// ---------------------------------------------------------------------------
// 2) Thread
// ---------------------------------------------------------------------------

function serverTimes(messages: readonly Message[]): string[] {
  return messages.filter((m) => !isPending(m)).map((m) => m.sentAt);
}

/** The open thread, oldest first (the API sends newest first; it is reversed by the merge). */
export function useConversationMessages(conversationId: string | null) {
  const qc = useQueryClient();
  const key = inboxKeys.thread(conversationId ?? '');
  return useQuery({
    queryKey: key,
    queryFn: async ({ signal }): Promise<ThreadData> => {
      const id = conversationId as string;
      const before = qc.getQueryData<ThreadData>(key);
      if (before?.syncedAt) {
        const since = pollSince(before.syncedAt);
        const changed = await readChanged((cursor) =>
          fetchMessages(id, { take: INBOX_MAX_TAKE, updatedAfter: since, cursor, signal }),
        );
        const latest = qc.getQueryData<ThreadData>(key) ?? before;
        if (changed.complete) {
          return {
            messages: mergeMessages(latest.messages, changed.items),
            olderCursor: latest.olderCursor,
            syncedAt: nextSyncedAt(latest.syncedAt, changed.serverTime, serverTimes(changed.items)),
          };
        }
        // More than POLL_MAX_PAGES pages changed: re-read the newest page and merge it, keeping the
        // older pages already on screen (a reload would drop them) and any reply waiting for its 202.
        const page = await fetchMessages(id, { take: THREAD_PAGE_TAKE, signal });
        return {
          messages: mergeMessages(mergeMessages(latest.messages, changed.items), page.items),
          olderCursor: latest.olderCursor,
          syncedAt: nextSyncedAt(null, page.serverTime, serverTimes(page.items)),
        };
      }
      const page = await fetchMessages(id, { take: THREAD_PAGE_TAKE, signal });
      // Keep any reply still waiting for its 202 (a reload must not lose it).
      const pending = (qc.getQueryData<ThreadData>(key)?.messages ?? []).filter(isPending);
      return {
        messages: mergeMessages(pending, page.items),
        olderCursor: page.nextCursor,
        syncedAt: nextSyncedAt(null, page.serverTime, serverTimes(page.items)),
      };
    },
    enabled: conversationId !== null,
    ...POLLING,
  });
}

/** Loads the next OLDER page of the thread and prepends it (merge by id). */
export function useLoadOlderMessages(conversationId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: [...inboxKeys.all, 'older'],
    mutationFn: async () => {
      if (!conversationId) return null;
      const cursor = qc.getQueryData<ThreadData>(inboxKeys.thread(conversationId))?.olderCursor;
      if (!cursor) return null;
      return { conversationId, page: await fetchMessages(conversationId, { take: THREAD_PAGE_TAKE, cursor }) };
    },
    onSuccess: (result) => {
      if (!result) return;
      qc.setQueryData<ThreadData>(inboxKeys.thread(result.conversationId), (prev) =>
        prev
          ? { ...prev, messages: mergeMessages(prev.messages, result.page.items), olderCursor: result.page.nextCursor }
          : prev,
      );
    },
    meta: { handledInline: true },
  });
}

// ---------------------------------------------------------------------------
// 3) Mark read
// ---------------------------------------------------------------------------

function setUnread(qc: QueryClient, conversationId: string, unreadCount: number) {
  qc.setQueriesData<ConversationListData>({ queryKey: inboxKeys.lists() }, (prev) =>
    prev
      ? { ...prev, items: prev.items.map((c) => (c.id === conversationId ? { ...c, unreadCount } : c)) }
      : prev,
  );
}

/**
 * `POST .../read`, optimistic: the row's unreadCount reads 0 at once. A failure puts the old count
 * back: an `updatedAfter` poll would not return the unchanged row, so it would stay at 0 on screen.
 */
export function useMarkConversationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: [...inboxKeys.all, 'read'],
    mutationFn: markConversationRead,
    onMutate: async (conversationId: string) => {
      await qc.cancelQueries({ queryKey: inboxKeys.lists() });
      const before = qc
        .getQueriesData<ConversationListData>({ queryKey: inboxKeys.lists() })
        .flatMap(([, data]) => data?.items ?? [])
        .find((c) => c.id === conversationId)?.unreadCount;
      setUnread(qc, conversationId, 0);
      return { before };
    },
    onError: (_error, conversationId, context) => {
      if (context?.before !== undefined) setUnread(qc, conversationId, context.before);
    },
    // A failed read mark is not worth a toast: the row simply stays unread.
    meta: { handledInline: true },
  });
}

// ---------------------------------------------------------------------------
// 4) Staff reply
// ---------------------------------------------------------------------------

interface SendVars {
  conversationId: string;
  body: string;
  clientMessageId: string;
}

interface FailedSend extends SendVars {
  failure: SendFailure;
}

function optimisticReply(vars: SendVars): Message {
  return {
    id: pendingId(vars.clientMessageId),
    externalMessageId: null,
    conversationId: vars.conversationId,
    appointmentId: null,
    direction: 'outbound',
    sentAt: new Date().toISOString(),
    body: vars.body,
    templateName: null,
    intent: null,
    confidence: null,
    language: null,
    provenance: 'human-staff',
    deliveryStatus: 'queued',
    failureReason: null,
  };
}

/**
 * A new idempotency key for one reply (contract: a uuid). `crypto.randomUUID` exists only in a
 * secure context (HTTPS or localhost); elsewhere a v4 uuid is built from `getRandomValues`.
 */
export function newClientMessageId(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const hex = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** What a send ended in. `restoreText`: put the text back in the composer (nothing was stored). */
export interface SendOutcome {
  ok: boolean;
  restoreText: boolean;
}

/**
 * Sends a staff reply (contract endpoint 4, Notifications #282) with an optimistic `queued`
 * bubble that the server's `Message` replaces (202: `sent`, or `failed` with failureReason;
 * later `delivered` / `read` arrive by polling).
 *
 * The `clientMessageId` is kept when the SAME reply (same conversation, same text) is sent
 * again after a failure whose outcome is unknown (network, 5xx), so a retry after a lost
 * response never sends twice. A definite refusal (409, 400, ...) gets a new id next time.
 *
 * On a failure the optimistic bubble is dropped. When the server stored a failed message
 * (409 opted_out / patient_communication_off, 503 channel_unavailable) the thread is re-read so
 * that message shows once; otherwise the text goes back in the composer. Failures are an
 * inline `failure` for the composer (no toast).
 */
export function useSendStaffReply(conversationId: string | null) {
  const qc = useQueryClient();
  const [failed, setFailed] = useState<FailedSend | null>(null);

  const mutation = useMutation({
    mutationKey: [...inboxKeys.all, 'send'],
    mutationFn: (vars: SendVars) =>
      sendStaffReply(vars.conversationId, { body: vars.body, clientMessageId: vars.clientMessageId }),
    onMutate: async (vars) => {
      setFailed(null);
      // A poll in flight would write back a thread without this bubble: cancel it first.
      await qc.cancelQueries({ queryKey: inboxKeys.thread(vars.conversationId) });
      qc.setQueryData<ThreadData>(inboxKeys.thread(vars.conversationId), (prev) =>
        prev ? { ...prev, messages: mergeMessages(prev.messages, [optimisticReply(vars)]) } : prev,
      );
    },
    onSuccess: async (message, vars) => {
      await qc.cancelQueries({ queryKey: inboxKeys.thread(vars.conversationId) });
      qc.setQueryData<ThreadData>(inboxKeys.thread(vars.conversationId), (prev) =>
        prev ? settlePending(prev, vars.clientMessageId, message) : prev,
      );
      // A 202 without a readable Message: the re-read brings it.
      if (!message.id) void qc.invalidateQueries({ queryKey: inboxKeys.thread(vars.conversationId) });
    },
    onError: (error, vars) => {
      const failure = describeSendError(error);
      qc.setQueryData<ThreadData>(inboxKeys.thread(vars.conversationId), (prev) =>
        prev ? dropPending(prev, vars.clientMessageId) : prev,
      );
      if (failure.storedOnServer) void qc.invalidateQueries({ queryKey: inboxKeys.thread(vars.conversationId) });
      setFailed({ ...vars, failure });
    },
    meta: { handledInline: true },
  });

  const current = failed && failed.conversationId === conversationId ? failed : null;

  /** Sends `body`. The failure, if any, is also in `failure`. */
  const send = async (body: string): Promise<SendOutcome> => {
    if (!conversationId) return { ok: false, restoreText: true };
    const reuse = current && current.body === body && current.failure.retryable;
    try {
      const clientMessageId = reuse ? current.clientMessageId : newClientMessageId();
      await mutation.mutateAsync({ conversationId, body, clientMessageId });
      return { ok: true, restoreText: false };
    } catch (error) {
      return { ok: false, restoreText: !describeSendError(error).storedOnServer };
    }
  };

  return {
    send,
    /** Sends the failed reply again (same clientMessageId when the failure was retryable). */
    retry: (): Promise<SendOutcome> => (current ? send(current.body) : Promise.resolve({ ok: false, restoreText: false })),
    failure: current?.failure ?? null,
    sending: mutation.isPending,
    dismiss: () => setFailed(null),
  };
}

